import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { Post, PostStatus } from "@/core/types/postTypes";
import type { MetricsByPlatform } from "@/components/PostCard";
import { UserRoleValue } from "@/services/urlService";

export type ValidStatuses = PostStatus;

// Wire + computed data structure (camelCase). The backend now emits camelCase
// for all JSON, so this single interface covers both the inbound wire shape and
// the computed values the model derives (MediaItem instances, defaults).
export interface PostItemData {
  id: string;
  clientId: string;
  postType: "standard" | "reel" | "story" | "post"; // Backend sends "post" for standard posts
  status: ValidStatuses;
  groupName: string | null;
  caption: string | null;
  hashtags: string | null;
  isCarousel: boolean;
  canBeInGrid: boolean;
  syncedToNotion: boolean;
  notionSynced?: boolean;
  notionPageId: string | null;
  createdAt: string;
  updatedAt: string;
  scheduledAt: string | null;
  publishedAt: string | null;
  clientRejectionReason: string | null;
  clientReviewedAt?: string | null;
  mediaItems: MediaItem[]; // Processed MediaItem instances
  gridItems: any[];
  notion?: any;
  // Reel-specific fields
  thumbnailMediaId?: string;
  isReel?: boolean;
  hasValidReelContent?: boolean;
  thumbnailMediaItem?: MediaItem; // Processed MediaItem instance
  // Post preparation status
  postFolderPreparationStatus?: "pending" | "preparing" | "ready" | "failed";
  // Human-readable reason when preparation failed (e.g. a missing original).
  postFolderPreparationError?: string | null;
  // Active grid position (computed by backend)
  activeGridPosition?: number | null;
  activeGridTotal?: number | null;
  // Public sharing
  publicStatus?: string | null;
  engagementScore?: number | null;
  // Ads / auto-boost signals
  isCommercial?: boolean;
  boostPriority?: number | null;
  metricsByPlatform?: MetricsByPlatform | null;
}

export class PostItem {
  // Raw wire data (camelCase), as passed in. Retained because clone() and
  // external call sites reference it directly.
  readonly backendData: PostItemData;
  // Computed view: wire data with nested media turned into MediaItem instances
  // and defaults applied.
  readonly frontendData: PostItemData;

  private context: "admin" | "client";
  private contextClientId: string;
  private authToken?: string;

  constructor(
    data: PostItemData,
    context: "admin" | "client",
    clientId: string,
    authToken?: string
  ) {
    this.backendData = { ...data };
    this.context = context;
    this.contextClientId = clientId;
    this.authToken = authToken;

    // The key mapping layer is gone — the wire is camelCase. Only domain logic
    // remains: building MediaItem instances for nested media and applying
    // defaults. Nested media may arrive as raw wire data (from fromBackend) or
    // as MediaItem instances (from fromLegacyPost); normalize both.
    this.frontendData = {
      ...data,
      syncedToNotion: data.syncedToNotion || data.notionSynced || false,
      clientReviewedAt: data.clientReviewedAt ?? null,
      mediaItems: (data.mediaItems || []).map(item =>
        MediaItem.fromBackend(
          item instanceof MediaItem ? item.backendData : item,
          this.context,
          this.contextClientId,
          this.authToken
        )
      ),
      gridItems: data.gridItems || [],
      isReel: data.isReel || data.postType === "reel",
      thumbnailMediaItem: data.thumbnailMediaItem
        ? MediaItem.fromBackend(
            data.thumbnailMediaItem instanceof MediaItem
              ? data.thumbnailMediaItem.backendData
              : data.thumbnailMediaItem,
            this.context,
            this.contextClientId,
            this.authToken
          )
        : undefined,
      metricsByPlatform: data.metricsByPlatform ?? null,
    };
  }

  // Getters
  get id(): string { return this.frontendData.id; }
  get clientId(): string { return this.frontendData.clientId; }
  get postType(): "standard" | "reel" | "story" | "post" { return this.frontendData.postType; }
  get status(): ValidStatuses { return this.frontendData.status; }
  get groupName(): string | null { return this.frontendData.groupName; }
  get caption(): string | null { return this.frontendData.caption; }
  get hashtags(): string | null { return this.frontendData.hashtags; }
  get isCarousel(): boolean { return this.frontendData.isCarousel; }
  get canBeInGrid(): boolean { return this.frontendData.canBeInGrid; }
  get syncedToNotion(): boolean { return this.frontendData.syncedToNotion; }
  get notionPageId(): string | null { return this.frontendData.notionPageId; }
  get createdAt(): string { return this.frontendData.createdAt; }
  get updatedAt(): string { return this.frontendData.updatedAt; }
  get scheduledAt(): string | null { return this.frontendData.scheduledAt; }
  get publishedAt(): string | null { return this.frontendData.publishedAt; }
  get clientRejectionReason(): string | null { return this.frontendData.clientRejectionReason; }
  get clientReviewedAt(): string | null | undefined { return this.frontendData.clientReviewedAt; }
  get mediaItems(): MediaItem[] { return this.frontendData.mediaItems; }
  get gridItems(): any[] { return this.frontendData.gridItems; }
  get notion(): any { return this.frontendData.notion; }
  get thumbnailMediaId(): string | undefined { return this.frontendData.thumbnailMediaId; }
  get isReel(): boolean { return this.frontendData.isReel || false; }
  get hasValidReelContent(): boolean { return this.frontendData.hasValidReelContent || false; }
  get thumbnailMediaItem(): MediaItem | undefined { return this.frontendData.thumbnailMediaItem; }
  get postFolderPreparationStatus(): "pending" | "preparing" | "ready" | "failed" | undefined { return this.frontendData.postFolderPreparationStatus; }
  get postFolderPreparationError(): string | null | undefined { return this.frontendData.postFolderPreparationError; }
  get publicStatus(): string | null | undefined { return this.frontendData.publicStatus; }
  get engagementScore(): number | null | undefined { return this.frontendData.engagementScore; }

  // Status check methods
  isDraft(): boolean { return this.status === "draft"; }
  isReady(): boolean { return this.status === "ready"; }
  isScheduled(): boolean { return this.status === "scheduled"; }
  isPartiallyPublished(): boolean { return this.status === "partially_published"; }
  isPublished(): boolean { return this.status === "published"; }
  isArchived(): boolean { return this.status === "archived"; }
  /** Legacy helper. The `client_rejected` status was removed in favor
   * of a status-neutral `client_reviewed_at` signal — always false now. */
  isRejected(): boolean { return false; }

  // Post type methods
  isStandardPost(): boolean { 
    // Backend might send "post" or "standard" for regular posts
    return this.postType === "standard" || this.postType === "post";
  }
  isReelPost(): boolean { return this.postType === "reel"; }
  isStoryPost(): boolean { return this.postType === "story"; }

  // Grid eligibility methods
  isGridEligible(): boolean {
    return (
      this.canBeInGrid &&
      (this.isStandardPost() || this.isReelPost()) &&
      this.mediaItems.length > 0
    );
  }

  // Media methods
  hasMedia(): boolean { return this.mediaItems.length > 0; }
  getMediaCount(): number { return this.mediaItems.length; }
  getFirstMediaItem(): MediaItem | undefined { return this.mediaItems[0]; }
  
  hasImages(): boolean { 
    return this.mediaItems.some(item => item.isImage());
  }
  
  hasVideos(): boolean { 
    return this.mediaItems.some(item => item.isVideo());
  }

  // Thumbnail methods for reels
  getThumbnailUrl(): string | undefined {
    if (this.thumbnailMediaItem) {
      return this.thumbnailMediaItem.getThumbnailUrlWithQueryAuth();
    }
    // Fallback to first media item if it's a video
    const firstVideo = this.mediaItems.find(item => item.isVideo());
    if (firstVideo) {
      return firstVideo.getThumbnailUrlWithQueryAuth();
    }
    // Fallback to first media item
    return this.getFirstMediaItem()?.getThumbnailUrlWithQueryAuth();
  }

  getDisplayUrl(): string | undefined {
    if (this.isReelPost()) {
      return this.getThumbnailUrl();
    }
    return this.getFirstMediaItem()?.getDisplayUrl();
  }

  // Status helper methods
  getAllValidStatuses(): ValidStatuses[] {
    return Object.values(PostStatus);
  }

  // Content validation
  isValid(): boolean {
    return this.hasMedia() && this.id.length > 0;
  }

  isReadyForGrid(): boolean {
    return this.isGridEligible() && this.isValid();
  }

  // Export methods
  toJSON(): PostItemData {
    return { ...this.frontendData };
  }

  // Legacy compatibility - returns the `Post` interface format. Now that both
  // shapes are camelCase this is a near-identity; the only adjustment is the
  // narrower `postType` union on `Post`.
  toLegacyPost(): Post {
    return {
      ...this.frontendData,
      postType: this.frontendData.postType as Post["postType"],
      notionSynced: this.frontendData.syncedToNotion,
    };
  }

  clone(updates: Partial<PostItemData> = {}): PostItem {
    return new PostItem(
      {
        ...this.backendData,
        ...updates,
      },
      this.context,
      this.contextClientId
    );
  }

  withStatus(newStatus: ValidStatuses): PostItem {
    return this.clone({ status: newStatus });
  }

  withMediaItems(mediaItems: MediaItem[]): PostItem {
    return this.clone({ mediaItems });
  }

  // Static factory methods
  static fromBackend(data: PostItemData, context: "admin" | "client", clientId: string): PostItem {
    return new PostItem(data, context, clientId);
  }

  static fromBackendArray(dataArray: PostItemData[], context: "admin" | "client", clientId: string): PostItem[] {
    return dataArray.map(item => PostItem.fromBackend(item, context, clientId));
  }

  // Builds a PostItem from the legacy `Post` shape. With both shapes now
  // camelCase the work is a single literal that defaults the fields `Post`
  // leaves optional but the model treats as required.
  static fromLegacyPost(legacyPost: Post, context: "admin" | "client", clientId: string, authToken?: string): PostItem {
    return new PostItem(
      {
        ...legacyPost,
        syncedToNotion: legacyPost.syncedToNotion ?? legacyPost.notionSynced ?? false,
        notionPageId: legacyPost.notionPageId ?? null,
        scheduledAt: legacyPost.scheduledAt ?? null,
        publishedAt: legacyPost.publishedAt ?? null,
        clientRejectionReason: legacyPost.clientRejectionReason ?? null,
        gridItems: legacyPost.gridItems ?? [],
      },
      context,
      clientId,
      authToken
    );
  }

  // Utility methods for filtering and sorting
  static sortByCreatedAt(posts: PostItem[], ascending = false): PostItem[] {
    return [...posts].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return ascending ? dateA - dateB : dateB - dateA;
    });
  }

  static sortByUpdatedAt(posts: PostItem[], ascending = false): PostItem[] {
    return [...posts].sort((a, b) => {
      const dateA = new Date(a.updatedAt).getTime();
      const dateB = new Date(b.updatedAt).getTime();
      return ascending ? dateA - dateB : dateB - dateA;
    });
  }

  static filterByStatus(posts: PostItem[], status: ValidStatuses): PostItem[] {
    return posts.filter(post => post.status === status);
  }

  static filterByPostType(posts: PostItem[], postType: "standard" | "reel" | "story" | "post"): PostItem[] {
    // Handle both "standard" and "post" as the same type
    if (postType === "standard" || postType === "post") {
      return posts.filter(post => post.postType === "standard" || post.postType === "post");
    }
    return posts.filter(post => post.postType === postType);
  }

  static filterGridEligible(posts: PostItem[]): PostItem[] {
    return posts.filter(post => post.isGridEligible());
  }

  static filterNotArchived(posts: PostItem[]): PostItem[] {
    return posts.filter(post => !post.isArchived());
  }

  static filterAvailableForGridSelection(posts: PostItem[]): PostItem[] {
    // For grid selection: exclude published and scheduled posts
    return posts.filter(post => 
      post.isGridEligible() && 
      post.status !== "published" && 
      post.status !== "scheduled"
    );
  }

  static groupByStatus(posts: PostItem[]): Record<ValidStatuses, PostItem[]> {
    return posts.reduce((groups, post) => {
      if (!groups[post.status]) {
        groups[post.status] = [];
      }
      groups[post.status].push(post);
      return groups;
    }, {} as Record<ValidStatuses, PostItem[]>);
  }
}